import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { getRenderQuality, PDF_PREVIEW_DPR, PDF_ZOOM_SETTLE_MS } from './renderQuality';
import {
  buildBalloonChip,
  displayMarkerLabel,
  markerBalloonScale,
  markerShowFields,
  offsetModeKey,
  resolveSavedLabelOffset,
} from './gaBalloonUtils';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

/**
 * GaDashboardCanvas - renders the GA drawing (PDF page or image) with an SVG
 * overlay of markers. Marker geometry is stored in coordinates normalized to
 * 0..1 of the drawing, so overlays survive any zoom or export resolution.
 *
 * Labels are presentation-style callouts: chips connected to their anchor by
 * a leader line. Three placement modes:
 *   - 'margins' (default): chips live in white gutters OUTSIDE the drawing,
 *     stacked left/right like a classic drawing legend - keeps the GA clean.
 *   - 'auto': chips float near their anchor with collision avoidance.
 *   - 'off': no labels.
 * In ANY mode the user can drag a chip anywhere (edit mode not required);
 * the position persists via style.labelOffset.
 */

export const STATUS_COLORS = {
  not_started: '#64748b',
  in_progress: '#f59e0b',
  complete: '#16a34a',
  late: '#dc2626',
  unbound: '#94a3b8',
};


/** Status of a marker at a specific "view date" (time scrubber). */
export function statusAtDate(resolved, viewDate) {
  if (!resolved || !resolved.start) return 'unbound';
  if (!viewDate) return resolved.status;
  if (viewDate < resolved.start) return 'not_started';
  if (resolved.finish && viewDate >= resolved.finish) return 'complete';
  return 'in_progress';
}

const anchorOf = (geometry) => {
  if (!geometry) return { x: 0, y: 0 };
  if (geometry.type === 'pin') return { x: geometry.x, y: geometry.y };
  if (geometry.type === 'rect') return { x: geometry.x + geometry.w / 2, y: geometry.y + geometry.h / 2 };
  if (geometry.type === 'polygon') {
    const xs = geometry.points.map((p) => p[0]);
    const ys = geometry.points.map((p) => p[1]);
    return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 };
  }
  return { x: 0, y: 0 };
};

// Estimated chip footprint in px (at drawing scale 1)
const CHIP_W_PX = 250;
const CHIP_H_PX = 96; // label + dates + optional baseline + labor units
const GUTTER_PX = 300; // white margin on each side in 'margins' mode — balloons live here

/**
 * Balloons, pins and leaders are sized in stage pixels, but the stage is as wide
 * as the drawing raster — the quality preset (1200–2000) for a PDF, the natural
 * width for an image. Sizing them against this reference keeps an annotation the
 * same size ON the drawing everywhere: the slide you scroll past, the slide you
 * are editing, and the exported PDF (which always renders at the full preset).
 */
const OVERLAY_REF_WIDTH = 1200;


/** Arrowhead at the drawing end of a leader line (points toward the region). */
const leaderArrowPoints = (x1, y1, x2, y2, size = 8) => {
  const angle = Math.atan2(y1 - y2, x1 - x2); // from balloon toward anchor
  const a1 = angle + Math.PI / 7;
  const a2 = angle - Math.PI / 7;
  return [
    `${x1},${y1}`,
    `${x1 - size * Math.cos(a1)},${y1 - size * Math.sin(a1)}`,
    `${x1 - size * Math.cos(a2)},${y1 - size * Math.sin(a2)}`,
  ].join(' ');
};

const GaDashboardCanvas = ({
  drawingUrl,
  previewUrl,
  drawingMime,
  drawingPage = 1,
  markers,
  resolvedById,
  compareById,
  viewDate,
  editMode,
  tool,
  selectedMarkerId,
  onSelectMarker,
  onCreateGeometry,
  onMoveMarker,
  onMoveLabel,
  labelMode = 'margins', // 'margins' | 'auto' | 'off'
  exportRef,
  /** Personal quality preset id (fast|balanced|high|ultra) — caps PDF raster density */
  renderQualityId = 'balanced',
  /** When false, wheel scrolls the parent (sequence stack) instead of zooming. */
  allowWheelZoom = true,
  /** Override the 300px balloon gutters (sequence slides should not grow 600px wider). */
  gutterPx,
  /** Inset used when fitting the stage to the container. Sequence uses a tight value. */
  fitPad = 30,
  /** Board-wide balloon scale (0.7–2). Per-marker style.balloonScale wins. */
  balloonScale = 1,
}) => {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 }); // rendered drawing px
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [draft, setDraft] = useState(null); // in-progress rect/polygon/move
  const [labelDraft, setLabelDraft] = useState(null); // { markerId, offset }
  const [fitted, setFitted] = useState(false);
  const [drawingReady, setDrawingReady] = useState(false);
  const dragState = useRef(null);

  const isPdf = (drawingMime || '').includes('pdf');
  const quality = getRenderQuality(renderQualityId);
  // Layout CSS width for PDFs. Pixel density = BASE_WIDTH * dpr (tracks zoom x screen DPR)
  // so zooming in re-rasters the page instead of CSS-stretching a soft bitmap.
  const BASE_WIDTH = quality.baseWidth;
  const MAX_PDF_DPR = quality.maxDpr;

  const targetPdfDpr = useMemo(() => {
    if (typeof window === 'undefined') return 1;
    const raw = Math.min(MAX_PDF_DPR, Math.max(1, (window.devicePixelRatio || 1) * zoom));
    return Math.round(raw * 4) / 4;
  }, [zoom, MAX_PDF_DPR]);

  /**
   * Progressive PDF layers (same idea as list-card thumb-sm → thumb-v2):
   *  - `front`: last fully-rendered bitmap (always visible; CSS-scaled while zooming)
   *  - `back`: optional sharper layer rendering off-screen; promoted when ready
   * Zoom-out never re-rasters (oversampled front just CSS-downscales — free).
   * Zoom-in: keep soft front, build sharp back, swap with no blank frame.
   */
  const layerKeyRef = useRef(0);
  const [pdfFront, setPdfFront] = useState(() => ({
    key: 0,
    dpr: Math.min(PDF_PREVIEW_DPR, typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1),
  }));
  const [pdfBack, setPdfBack] = useState(null); // { key, dpr } | null
  const pdfFrontDprRef = useRef(pdfFront.dpr);
  pdfFrontDprRef.current = pdfFront.dpr;
  /** Layer + quality of the raster actually on screen — what export waits on. */
  const [painted, setPainted] = useState(null); // { key, quality } | null
  const qualityRef = useRef(renderQualityId);
  qualityRef.current = renderQualityId;

  const queuePdfUpgrade = useCallback((frontDpr, desiredDpr) => {
    const rounded = Math.round(desiredDpr * 4) / 4;
    // Already sharp enough or oversampled (zoom-out) — keep front, cancel back.
    if (frontDpr + 0.01 >= rounded) {
      setPdfBack(null);
      return;
    }
    setPdfBack((prev) => {
      if (prev && Math.abs(prev.dpr - rounded) < 0.01) return prev;
      layerKeyRef.current += 1;
      return { key: layerKeyRef.current, dpr: rounded };
    });
  }, []);

  // After zoom settles: upgrade sharpness if needed (preview → target chained).
  useEffect(() => {
    if (!isPdf) return undefined;
    const preview = Math.min(PDF_PREVIEW_DPR, targetPdfDpr);
    const t = setTimeout(() => {
      const frontDpr = pdfFrontDprRef.current;
      const next = frontDpr + 0.01 < preview ? preview : targetPdfDpr;
      queuePdfUpgrade(frontDpr, next);
    }, PDF_ZOOM_SETTLE_MS);
    return () => clearTimeout(t);
  }, [targetPdfDpr, isPdf, queuePdfUpgrade]);

  // When preview promotes and we're still below target, queue the sharp layer.
  useEffect(() => {
    if (!isPdf || pdfBack != null) return undefined;
    if (pdfFront.dpr + 0.01 >= targetPdfDpr) return undefined;
    const preview = Math.min(PDF_PREVIEW_DPR, targetPdfDpr);
    // Still below preview — the settle effect owns that step.
    if (pdfFront.dpr + 0.01 < preview) return undefined;
    const t = setTimeout(() => queuePdfUpgrade(pdfFront.dpr, targetPdfDpr), 40);
    return () => clearTimeout(t);
  }, [pdfFront.dpr, pdfBack, targetPdfDpr, isPdf, queuePdfUpgrade]);

  const promotePdfBack = useCallback(() => {
    setPdfBack((back) => {
      if (!back) return null;
      setPdfFront(back); // same key → canvas stays mounted, just becomes visible
      setPainted({ key: back.key, quality: qualityRef.current });
      return null;
    });
  }, []);

  useEffect(() => {
    setDrawingReady(false);
    setPainted(null);
  }, [drawingUrl]);

  // Quality preset or drawing change: reset to preview, then progressive upgrade.
  useEffect(() => {
    if (!isPdf) return;
    layerKeyRef.current += 1;
    const preview = Math.min(PDF_PREVIEW_DPR, targetPdfDpr);
    setPdfFront({ key: layerKeyRef.current, dpr: preview });
    setPdfBack(null);
    setSize((prev) => {
      if (!prev.w || !prev.h) return prev;
      const aspect = prev.h / prev.w;
      return { w: BASE_WIDTH, h: BASE_WIDTH * aspect };
    });
  }, [MAX_PDF_DPR, BASE_WIDTH, isPdf, drawingUrl, drawingPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const showLabels = labelMode !== 'off';
  const overlayScale = (size.w || BASE_WIDTH) / OVERLAY_REF_WIDTH;
  const gutter = gutterPx == null ? GUTTER_PX * overlayScale : Math.max(0, gutterPx);
  const padX = labelMode === 'margins' ? gutter : 0;
  const padY = labelMode === 'margins' && gutter > 0 ? 16 * overlayScale : 0;
  const stageW = (size.w || BASE_WIDTH) + padX * 2;
  const stageH = (size.h || 900) + padY * 2;

  // Export readiness. `drawingReady` only means "a page painted at some point",
  // and it stays true while a quality change swaps in a fresh <Page> — a capture
  // taken in that gap gets the balloons with no drawing behind them. The stage
  // counts as ready only when the raster on screen belongs to the mounted layer
  // AND the requested quality, with no sharper layer still building.
  const stageReady = isPdf
    ? !!painted && painted.key === pdfFront.key && painted.quality === renderQualityId && !pdfBack
    : drawingReady;

  // ---- fit-to-container ----
  const fitToContainer = useCallback((w, h) => {
    const el = containerRef.current;
    if (!el || !w || !h) return;
    const pad = Math.max(0, fitPad);
    const z = Math.min((el.clientWidth - pad) / w, (el.clientHeight - pad) / h);
    setZoom(Math.max(0.1, Math.min(2, z)));
    setPan({ x: 0, y: 0 });
  }, [fitPad]);

  const applySize = (w, h) => {
    setSize({ w, h });
    if (!fitted) {
      setFitted(true);
      requestAnimationFrame(() => fitToContainer(w + padX * 2, h + padY * 2));
    }
  };

  const onPreviewLoad = (e) => {
    const img = e.target;
    const nw = img.naturalWidth || BASE_WIDTH;
    const nh = img.naturalHeight || BASE_WIDTH;
    if (!nw || !nh) return;
    applySize(BASE_WIDTH, (nh / nw) * BASE_WIDTH);
  };
  const onImgLoad = (e) => {
    const img = e.target;
    // Prefer native pixels (capped) so zoom doesn't upsample a 1600px downscale
    const nw = img.naturalWidth || BASE_WIDTH;
    const nh = img.naturalHeight || BASE_WIDTH;
    const MAX_IMG_LAYOUT = 3200;
    const w = Math.min(MAX_IMG_LAYOUT, nw);
    applySize(w, (nh / nw) * w);
    setDrawingReady(true);
  };
  const onPdfPageLoad = (page) => {
    const vp = page.getViewport({ scale: 1 });
    applySize(BASE_WIDTH, (vp.height / vp.width) * BASE_WIDTH);
  };
  const onFrontPdfRender = () => {
    setDrawingReady(true);
    setPainted({ key: pdfFront.key, quality: renderQualityId });
  };

  // ---- coordinate helpers (normalized to the DRAWING, not the stage) ----
  const toNorm = useCallback((e) => {
    const el = containerRef.current?.querySelector('[data-ga-drawing]');
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  }, []);

  const clamp01 = (v) => Math.min(1, Math.max(0, v));

  const translateGeometry = (g, dx, dy) => {
    if (g.type === 'pin') return { ...g, x: g.x + dx, y: g.y + dy };
    if (g.type === 'rect') return { ...g, x: g.x + dx, y: g.y + dy };
    if (g.type === 'polygon') return { ...g, points: g.points.map(([x, y]) => [x + dx, y + dy]) };
    return g;
  };

  // ---- label layout ----
  const scaleOf = (m) => markerBalloonScale(m, balloonScale) * overlayScale;
  const maxBalloonScale = markers.reduce((acc, m) => Math.max(acc, scaleOf(m)), (balloonScale || 1) * overlayScale);
  const chipWN = size.w ? (CHIP_W_PX * maxBalloonScale) / size.w : 0.13 * maxBalloonScale;
  const chipHN = size.h ? (CHIP_H_PX * maxBalloonScale) / size.h : 0.05 * maxBalloonScale;

  // Resolve a marker's saved balloon position for the ACTIVE mode only.
  // style.labelOffsets = { margins: {dx,dy}, auto: {dx,dy} }; legacy style.labelOffset
  // is honored when style.labelOffsetMode matches (or is missing, for old data).
  // Sequence slides draw as 'auto' with no gutters — honor older `margins`
  // saves so a drop is not discarded (that was the bounce-back).
  const savedOffsetFor = (m) => resolveSavedLabelOffset(m, {
    labelMode,
    labelDraft,
    fallbackToMargins: labelMode === 'auto' && gutter === 0,
  });

  const labelOffsets = useMemo(() => {
    if (!size.h || !showLabels) return {};
    const offsets = {};
    const autoMarkers = [];
    for (const m of markers) {
      const manual = savedOffsetFor(m);
      if (manual && typeof manual.dx === 'number') offsets[m.id] = manual;
      else autoMarkers.push(m);
    }

    if (labelMode === 'margins') {
      // Chips in left/right gutters. Prefer the side of the anchor, but if one side
      // would overflow, spill to the other so balloons never pile on each other.
      const spacing = chipHN * 1.35;
      const candidates = autoMarkers
        .map((m) => ({ m, a: anchorOf(m.geometry) }))
        .sort((p, q) => p.a.y - q.a.y || p.a.x - q.a.x);
      const sides = { left: [], right: [] };
      // Balance left/right so leaders don't all crowd one gutter; still prefer
      // the geometric side when the two sides have equal counts.
      for (const p of candidates) {
        const prefer = p.a.x < 0.5 ? 'left' : 'right';
        const other = prefer === 'left' ? 'right' : 'left';
        if (sides[prefer].length <= sides[other].length) sides[prefer].push(p);
        else sides[other].push(p);
      }
      for (const side of ['left', 'right']) {
        const list = sides[side];
        if (!list.length) continue;
        // Evenly distribute down the gutter so leaders stay readable
        const usable = Math.max(spacing, 1 - spacing);
        const step = list.length === 1 ? 0 : usable / (list.length - 1);
        const gutterFrac = (gutter - 20 * overlayScale) / size.w;
        const cx = side === 'left' ? -gutterFrac / 2 : 1 + gutterFrac / 2;
        list.forEach((p, i) => {
          const y = spacing + i * step;
          offsets[p.m.id] = { dx: cx - p.a.x, dy: y - p.a.y };
        });
      }
      return offsets;
    }

    // 'auto' mode: greedy collision-avoiding fan around the anchor
    const placed = [];
    const overlaps = (box) =>
      placed.some((p) => box.x < p.x + p.w && box.x + box.w > p.x && box.y < p.y + p.h && box.y + box.h > p.y);
    const boxFor = (a, off) => ({ x: a.x + off.dx - chipWN / 2, y: a.y + off.dy - chipHN, w: chipWN, h: chipHN });
    for (const id of Object.keys(offsets)) {
      const m = markers.find((mm) => mm.id === Number(id) || mm.id === id);
      if (m) placed.push(boxFor(anchorOf(m.geometry), offsets[id]));
    }
    const CANDIDATES = [];
    for (let ring = 1; ring <= 6; ring++) {
      const r = 0.035 + ring * 0.035;
      CANDIDATES.push({ dx: 0, dy: -r });
      CANDIDATES.push({ dx: r * 1.6, dy: -r * 0.7 });
      CANDIDATES.push({ dx: -r * 1.6, dy: -r * 0.7 });
      CANDIDATES.push({ dx: r * 1.8, dy: r * 0.3 + chipHN });
      CANDIDATES.push({ dx: -r * 1.8, dy: r * 0.3 + chipHN });
      CANDIDATES.push({ dx: 0, dy: r + chipHN });
    }
    const sorted = [...autoMarkers].sort((m1, m2) => anchorOf(m1.geometry).y - anchorOf(m2.geometry).y);
    for (const m of sorted) {
      const a = anchorOf(m.geometry);
      let chosen = CANDIDATES[0];
      for (const c of CANDIDATES) {
        const box = boxFor(a, c);
        if (box.x < 0.005 || box.x + box.w > 0.995 || box.y < 0.005 || box.y + box.h > 0.99) continue;
        if (!overlaps(box)) { chosen = c; break; }
      }
      offsets[m.id] = chosen;
      placed.push(boxFor(a, chosen));
    }
    return offsets;
    // savedOffsetFor closes over labelMode + labelDraft + markers + gutter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, size.w, size.h, labelDraft, labelMode, showLabels, chipWN, chipHN, gutter, balloonScale]);

  // ---- polygon drafting helpers ----
  // Threshold (in drawing-normalized units) for "did I click on the first
  // vertex?" — a constant ~12 screen px regardless of zoom level.
  const polyCloseThreshold = () => ({
    x: 12 / Math.max(1, (size.w || BASE_WIDTH) * zoom),
    y: 12 / Math.max(1, (size.h || 900) * zoom),
  });

  const nearFirstVertex = (pts, p) => {
    if (!pts || pts.length < 3) return false;
    const th = polyCloseThreshold();
    return Math.abs(p.x - pts[0][0]) < th.x && Math.abs(p.y - pts[0][1]) < th.y;
  };

  /** Finish the in-progress polygon: drop near-duplicate consecutive points
   *  (double-click fires two mousedowns first), drop a last point that sits on
   *  the first, and only create the marker if 3+ distinct vertices remain. */
  const finishPolygonDraft = () => {
    if (draft?.type !== 'polygon') return;
    const th = polyCloseThreshold();
    const pts = [];
    for (const pt of draft.points) {
      const last = pts[pts.length - 1];
      if (!last || Math.abs(pt[0] - last[0]) > th.x || Math.abs(pt[1] - last[1]) > th.y) pts.push(pt);
    }
    if (pts.length >= 4 && nearFirstVertex(pts, { x: pts[pts.length - 1][0], y: pts[pts.length - 1][1] })) pts.pop();
    if (pts.length >= 3) {
      onCreateGeometry({ type: 'polygon', points: pts }, 'zone');
      setDraft(null);
    }
    // Fewer than 3 distinct points: keep the draft so the user can keep clicking.
  };

  // Cancel a half-drawn polygon if the user switches tool or leaves edit mode —
  // otherwise the dashed outline stays stuck on screen with no way to finish it.
  useEffect(() => {
    setDraft((prev) => (prev?.type === 'polygon' ? null : prev));
  }, [tool, editMode]);

  // ---- mouse interactions ----
  const handleStageMouseDown = (e) => {
    if (!editMode || tool === 'select') {
      dragState.current = { mode: 'pan', startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
      return;
    }
    const p = toNorm(e);
    if (!p) return;
    if (tool === 'pin' || tool === 'callout') {
      onCreateGeometry({ type: 'pin', x: clamp01(p.x), y: clamp01(p.y) }, tool);
    } else if (tool === 'rect') {
      setDraft({ type: 'rect', x: p.x, y: p.y, w: 0, h: 0, startX: p.x, startY: p.y });
      dragState.current = { mode: 'rect' };
    } else if (tool === 'polygon') {
      // Second mousedown of a double-click — onDoubleClick closes the shape;
      // don't add another (duplicate) vertex.
      if (e.detail >= 2) return;
      if (draft?.type === 'polygon') {
        if (nearFirstVertex(draft.points, p)) {
          finishPolygonDraft();
          return;
        }
        setDraft({ ...draft, points: [...draft.points, [p.x, p.y]] });
      } else {
        setDraft({ type: 'polygon', points: [[p.x, p.y]] });
      }
    }
  };

  const handleMouseMove = (e) => {
    // Rubber-band preview while drawing a polygon (no drag in progress)
    if (!dragState.current && tool === 'polygon' && draft?.type === 'polygon') {
      const p = toNorm(e);
      if (p) setDraft((prev) => (prev?.type === 'polygon' ? { ...prev, cursor: [p.x, p.y] } : prev));
      return;
    }
    const ds = dragState.current;
    if (!ds) return;
    if (ds.mode === 'pan') {
      setPan({ x: ds.panX + (e.clientX - ds.startX), y: ds.panY + (e.clientY - ds.startY) });
      return;
    }
    const p = toNorm(e);
    if (!p) return;
    if (ds.mode === 'rect' && draft) {
      setDraft((prev) => ({
        ...prev,
        x: Math.min(prev.startX, p.x),
        y: Math.min(prev.startY, p.y),
        w: Math.abs(p.x - prev.startX),
        h: Math.abs(p.y - prev.startY),
      }));
    } else if (ds.mode === 'move') {
      const dx = p.x - ds.grabX;
      const dy = p.y - ds.grabY;
      ds.moved = true;
      ds.lastGeometry = translateGeometry(ds.geometry, dx, dy);
      setDraft({ type: 'moving', markerId: ds.markerId, geometry: ds.lastGeometry });
    } else if (ds.mode === 'label') {
      const a = anchorOf(ds.geometry);
      ds.moved = true;
      ds.lastOffset = { dx: p.x - a.x - ds.grabDx, dy: p.y - a.y - ds.grabDy };
      setLabelDraft({ markerId: ds.markerId, offset: ds.lastOffset });
    }
  };

  const handleMouseUp = () => {
    const ds = dragState.current;
    if (!ds) return;
    dragState.current = null;
    if (ds.mode === 'rect' && draft && draft.w > 0.005 && draft.h > 0.005) {
      const { startX, startY, ...geom } = draft;
      onCreateGeometry(geom, 'zone');
      setDraft(null);
    } else if (ds.mode === 'rect') {
      setDraft(null);
    } else if (ds.mode === 'move') {
      if (ds.moved && ds.lastGeometry) onMoveMarker(ds.markerId, ds.lastGeometry);
      else onSelectMarker(ds.markerId);
      setDraft(null);
    } else if (ds.mode === 'label') {
      if (ds.moved && ds.lastOffset && onMoveLabel) {
        onMoveLabel(ds.markerId, ds.lastOffset, offsetModeKey(labelMode));
      } else {
        onSelectMarker(ds.markerId);
      }
      setLabelDraft(null);
    }
  };

  // Ending a label drag on mouseleave used to drop the balloon back if the
  // pointer crossed the PDF canvas. Pointer-capture owns label drags.
  const handleContainerLeave = () => {
    if (dragState.current?.mode === 'label') return;
    handleMouseUp();
  };

  const handleDoubleClick = () => {
    if (tool === 'polygon' && draft?.type === 'polygon') finishPolygonDraft();
  };

  // Keyboard while drawing a polygon: Enter closes the shape, Escape cancels it.
  useEffect(() => {
    if (!(editMode && tool === 'polygon' && draft?.type === 'polygon')) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setDraft(null);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        finishPolygonDraft();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // finishPolygonDraft closes over the current draft
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, tool, draft]);

  const handleMarkerMouseDown = (e, marker) => {
    e.stopPropagation();
    if (!editMode) {
      onSelectMarker(marker.id);
      return;
    }
    const p = toNorm(e);
    dragState.current = {
      mode: 'move', markerId: marker.id, geometry: marker.geometry,
      grabX: p.x, grabY: p.y, moved: false,
    };
  };

  // Balloons can be dragged in ANY mode (view or edit)
  const handleLabelPointerDown = (e, marker) => {
    e.stopPropagation();
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    const p = toNorm(e);
    if (!p) return;
    const a = anchorOf(marker.geometry);
    const cur = labelOffsets[marker.id] || { dx: 0, dy: 0 };
    dragState.current = {
      mode: 'label', markerId: marker.id, geometry: marker.geometry, moved: false,
      pointerId: e.pointerId,
      // remember where inside the chip the user grabbed, so it doesn't jump
      grabDx: p.x - a.x - cur.dx, grabDy: p.y - a.y - cur.dy,
    };
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    setZoom((z) => Math.min(8, Math.max(0.1, z * factor)));
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !allowWheelZoom) return undefined;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowWheelZoom]);

  // Refit when gutters change or the slot is resized (sequence pages fill width)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const refit = () => {
      if (size.w) fitToContainer(size.w + padX * 2, size.h + padY * 2);
    };
    refit();
    const ro = new ResizeObserver(refit);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelMode, gutter, fitPad, size.w, size.h]);

  // ---- rendering ----
  const markerColor = (marker, resolved) => {
    const custom = marker.style?.color;
    const status = statusAtDate(resolved, viewDate);
    if (custom && (!resolved || status === 'unbound')) return custom;
    return STATUS_COLORS[status] || custom || '#64748b';
  };

  // Drawing-normalized -> stage px
  const X = (x) => x * size.w + padX;
  const Y = (y) => y * size.h + padY;

  const renderShape = (marker, geometry, color, isSelected, isDraft = false) => {
    const stroke = isSelected ? '#2563eb' : color;
    const strokeW = (isSelected ? 3 : 2) * overlayScale;
    const fillOpacity = marker?.style?.fillOpacity ?? 0.22;
    if (geometry.type === 'rect') {
      return (
        <rect
          x={X(geometry.x)} y={Y(geometry.y)} width={geometry.w * size.w} height={geometry.h * size.h}
          fill={color} fillOpacity={fillOpacity} stroke={stroke} strokeWidth={strokeW}
          strokeDasharray={isDraft ? '6 4' : undefined} rx={4}
          style={{ cursor: editMode ? 'move' : 'pointer' }}
          onMouseDown={marker ? (e) => handleMarkerMouseDown(e, marker) : undefined}
        />
      );
    }
    if (geometry.type === 'polygon') {
      const pts = geometry.points.map(([x, y]) => `${X(x)},${Y(y)}`).join(' ');
      return (
        <polygon
          points={pts} fill={color} fillOpacity={fillOpacity} stroke={stroke} strokeWidth={strokeW}
          strokeDasharray={isDraft ? '6 4' : undefined}
          style={{ cursor: editMode ? 'move' : 'pointer' }}
          onMouseDown={marker ? (e) => handleMarkerMouseDown(e, marker) : undefined}
        />
      );
    }
    const px = X(geometry.x);
    const py = Y(geometry.y);
    return (
      <g
        style={{ cursor: editMode ? 'move' : 'pointer' }}
        onMouseDown={marker ? (e) => handleMarkerMouseDown(e, marker) : undefined}
      >
        <circle cx={px} cy={py} r={(isSelected ? 11 : 9) * overlayScale} fill={color} stroke="#fff" strokeWidth={2.5 * overlayScale} />
        <circle cx={px} cy={py} r={3 * overlayScale} fill="#fff" />
      </g>
    );
  };

  const currentGeometry = (m) =>
    draft?.type === 'moving' && draft.markerId === m.id ? draft.geometry : m.geometry;

  const renderLeader = (marker, resolved) => {
    if (!showLabels) return null;
    const off = labelOffsets[marker.id];
    if (!off) return null;
    const a = anchorOf(currentGeometry(marker));
    const color = markerColor(marker, resolved);
    const x1 = X(a.x);
    const y1 = Y(a.y);
    // leader ends at the chip's bottom-center
    const x2 = X(a.x + off.dx);
    const y2 = Y(a.y + off.dy);
    if (Math.abs(x2 - x1) < 12 * overlayScale && Math.abs(y2 - y1) < 26 * overlayScale) return null;
    return (
      <g key={`leader-${marker.id}`} pointerEvents="none">
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={1.5 * overlayScale} opacity={0.85} />
        <polygon points={leaderArrowPoints(x1, y1, x2, y2, 8 * overlayScale)} fill={color} opacity={0.95} />
        <circle cx={x2} cy={y2} r={2.5 * overlayScale} fill={color} opacity={0.7} />
      </g>
    );
  };

  const renderLabel = (marker, resolved, delta) => {
    if (!showLabels) return null;
    const off = labelOffsets[marker.id];
    if (!off) return null;
    const fields = markerShowFields(marker);
    const scale = scaleOf(marker);
    const a = anchorOf(currentGeometry(marker));
    const status = statusAtDate(resolved, viewDate);
    const color = markerColor(marker, resolved);
    const isSelected = selectedMarkerId === marker.id;
    const fill = marker.style?.fillColor || 'rgba(255,255,255,0.97)';
    const borderColor = marker.style?.color || color;
    const titleText = displayMarkerLabel(marker, resolved);
    const chip = buildBalloonChip({
      title: titleText,
      fields,
      resolved,
      delta,
      late: status === 'late',
      color,
      fill,
      borderColor,
      scale,
    });
    const left = X(a.x + off.dx);
    const top = Y(a.y + off.dy);
    return (
      <div
        key={`label-${marker.id}`}
        data-ga-balloon={JSON.stringify(chip)}
        data-ga-cx={left}
        data-ga-cy={top}
        onPointerDown={(e) => handleLabelPointerDown(e, marker)}
        onPointerUp={handleMouseUp}
        title="Drag to move this balloon — position is saved"
        style={{
          position: 'absolute',
          left,
          top,
          transform: 'translate(-50%, -100%)',
          background: fill,
          border: `${Math.max(1.5, 2 * scale)}px solid ${borderColor}`,
          borderRadius: 7 * scale,
          padding: `${3 * scale}px ${7 * scale}px ${3 * scale}px ${5 * scale}px`,
          fontSize: 11 * scale,
          boxShadow: isSelected
            ? '0 0 0 3px rgba(37,99,235,0.35), 0 2px 6px rgba(0,0,0,0.3)'
            : '0 2px 6px rgba(0,0,0,0.3)',
          whiteSpace: 'nowrap',
          pointerEvents: 'auto',
          cursor: 'grab',
          touchAction: 'none',
          zIndex: isSelected ? 6 : 3,
          display: 'block',
          boxSizing: 'border-box',
        }}
      >
        {chip.lines.map((line, i) => (
          <div
            key={`${line.kind}-${i}`}
            style={{
              display: 'block',
              fontWeight: line.fontWeight,
              fontSize: line.fontSize * scale,
              lineHeight: `${Math.round(line.fontSize * scale * 1.4)}px`,
              color: line.color,
              fontStyle: line.italic ? 'italic' : 'normal',
              whiteSpace: 'nowrap',
            }}
          >
            {line.kind === 'title' && (
              <>
                <span
                  style={{
                    color: '#94a3b8',
                    fontSize: 10 * scale,
                    letterSpacing: -1,
                    lineHeight: `${Math.round(line.fontSize * scale * 1.4)}px`,
                    verticalAlign: 'middle',
                    marginRight: 4 * scale,
                  }}
                  aria-hidden
                >⠿</span>
                <span
                  style={{
                    width: 8 * scale,
                    height: 8 * scale,
                    borderRadius: '50%',
                    background: color,
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    marginRight: 5 * scale,
                  }}
                />
              </>
            )}
            <span style={{ verticalAlign: 'middle' }}>{line.text}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleContainerLeave}
      onPointerMove={handleMouseMove}
      onPointerUp={handleMouseUp}
      onPointerCancel={handleMouseUp}
      style={{
        flex: 1, overflow: 'hidden', position: 'relative', background: '#e2e8f0',
        cursor: editMode && tool !== 'select' ? 'crosshair' : dragState.current?.mode === 'pan' ? 'grabbing' : 'grab',
        userSelect: 'none',
      }}
    >
      {/* Polygon drawing instructions */}
      {editMode && tool === 'polygon' && (
        <div style={{
          position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 10,
          background: 'rgba(15,23,42,0.88)', color: '#fff', borderRadius: 8, padding: '6px 14px',
          fontSize: 12.5, fontWeight: 500, boxShadow: '0 2px 8px rgba(0,0,0,0.3)', whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}>
          {draft?.type === 'polygon'
            ? (draft.points.length >= 3
              ? 'Click the ⭘ first point (or double-click / Enter) to close — Esc cancels'
              : `Click to add points (${draft.points.length}/3 minimum) — Esc cancels`)
            : 'Click on the drawing to place the first corner of the polygon'}
        </div>
      )}

      {/* Zoom controls */}
      <div style={{ position: 'absolute', right: 12, bottom: 12, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {[['+', () => setZoom((z) => Math.min(8, z * 1.25))],
          ['−', () => setZoom((z) => Math.max(0.1, z / 1.25))],
          ['⤢', () => fitToContainer(stageW, stageH)]].map(([txt, fn]) => (
          <button key={txt} onClick={fn} title={txt === '⤢' ? 'Fit to screen' : undefined} style={{
            width: 34, height: 34, borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff',
            fontSize: 16, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
          }}>{txt}</button>
        ))}
      </div>

      <div style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: 'center', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div
          data-ga-stage
          data-ga-ready={stageReady ? '1' : '0'}
          data-ga-quality={renderQualityId}
          ref={exportRef}
          onMouseDown={handleStageMouseDown}
          onDoubleClick={handleDoubleClick}
          style={{ position: 'relative', width: stageW, height: stageH, flexShrink: 0, background: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
        >
          {/* The drawing itself, inset by the gutters */}
          <div data-ga-drawing style={{ position: 'absolute', left: padX, top: padY, width: size.w || BASE_WIDTH, height: size.h || 'auto' }}>
            <div style={{ position: 'relative', width: size.w || BASE_WIDTH }}>
              {previewUrl && !drawingReady && (
                <img
                  src={previewUrl}
                  alt=""
                  onLoad={onPreviewLoad}
                  draggable={false}
                  style={{ width: '100%', height: 'auto', display: 'block', background: '#fff' }}
                />
              )}
              {drawingUrl && isPdf && (
                <div style={{
                  position: drawingReady || !previewUrl ? 'relative' : 'absolute',
                  left: 0, top: 0, width: '100%',
                  opacity: drawingReady || !previewUrl ? 1 : 0,
                }}>
                  <Document file={drawingUrl} loading={previewUrl ? null : <div style={{ padding: 40 }}>Loading drawing…</div>}>
                    <div style={{ position: 'relative', width: BASE_WIDTH }}>
                      {[pdfFront, pdfBack].filter(Boolean).map((layer) => {
                        const isFront = layer.key === pdfFront.key;
                        const isBuilding = pdfBack != null && layer.key === pdfBack.key;
                        return (
                          <div
                            key={layer.key}
                            aria-hidden={!isFront}
                            style={{
                              position: isFront ? 'relative' : 'absolute',
                              left: isFront ? undefined : 0,
                              top: isFront ? undefined : 0,
                              width: BASE_WIDTH,
                              opacity: isFront ? 1 : 0,
                              pointerEvents: 'none',
                            }}
                          >
                            <Page
                              pageNumber={drawingPage}
                              width={BASE_WIDTH}
                              devicePixelRatio={layer.dpr}
                              onLoadSuccess={isFront ? onPdfPageLoad : undefined}
                              onRenderSuccess={isFront ? onFrontPdfRender : (isBuilding ? promotePdfBack : undefined)}
                              onRenderError={isBuilding ? (() => setPdfBack(null)) : undefined}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </Document>
                </div>
              )}
              {drawingUrl && !isPdf && (
                <img
                  src={drawingUrl}
                  alt="GA drawing"
                  style={{
                    width: size.w || '100%',
                    height: size.h || 'auto',
                    display: drawingReady || !previewUrl ? 'block' : 'none',
                  }}
                  onLoad={onImgLoad}
                  draggable={false}
                />
              )}
              {previewUrl && !drawingReady && (
                <div
                  title="Loading full drawing…"
                  style={{
                    position: 'absolute', right: 8, bottom: 8, zIndex: 2,
                    fontSize: 11, fontWeight: 600, color: '#0f766e',
                    background: 'rgba(255,255,255,0.88)', border: '1px solid #99f6e4',
                    borderRadius: 999, padding: '2px 8px', pointerEvents: 'none',
                  }}
                >
                  Loading drawing…
                </div>
              )}
              {drawingReady && pdfBack && (
                <div
                  title="Sharpening…"
                  style={{
                    position: 'absolute', right: 8, bottom: 8, zIndex: 2,
                    fontSize: 11, fontWeight: 600, color: '#0f766e',
                    background: 'rgba(255,255,255,0.88)', border: '1px solid #99f6e4',
                    borderRadius: 999, padding: '2px 8px', pointerEvents: 'none',
                  }}
                >
                  Sharpening…
                </div>
              )}
            </div>
          </div>

          {size.h > 0 && (
            <svg
              width={stageW} height={stageH} viewBox={`0 0 ${stageW} ${stageH}`}
              style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: editMode && tool !== 'select' ? 'none' : 'auto' }}
            >
              {/* Leader lines under the shapes */}
              {markers.map((m) => renderLeader(m, resolvedById[m.id]))}
              {markers.map((m) => (
                <g key={m.id} style={{ pointerEvents: 'auto' }}>
                  {renderShape(m, currentGeometry(m), markerColor(m, resolvedById[m.id]), selectedMarkerId === m.id)}
                </g>
              ))}
              {draft && draft.type === 'rect' && renderShape(null, draft, '#2563eb', false, true)}
              {draft && draft.type === 'polygon' && (() => {
                const pts = draft.points;
                const canClose = pts.length >= 3;
                const [fx, fy] = pts[0];
                const [lx, ly] = pts[pts.length - 1];
                const path = pts.map(([x, y]) => `${X(x)},${Y(y)}`).join(' ');
                return (
                  <g pointerEvents="none">
                    {/* fill preview of the shape as it would close right now */}
                    {canClose && <polygon points={path} fill="#2563eb" fillOpacity={0.1} stroke="none" />}
                    <polyline points={path} fill="none" stroke="#2563eb" strokeWidth={2} strokeDasharray="6 4" />
                    {/* rubber band from last vertex to the cursor */}
                    {draft.cursor && (
                      <line
                        x1={X(lx)} y1={Y(ly)} x2={X(draft.cursor[0])} y2={Y(draft.cursor[1])}
                        stroke="#2563eb" strokeWidth={1.5} strokeDasharray="4 4" opacity={0.55}
                      />
                    )}
                    {/* closing edge preview back to the first vertex */}
                    {canClose && (
                      <line
                        x1={X(lx)} y1={Y(ly)} x2={X(fx)} y2={Y(fy)}
                        stroke="#2563eb" strokeWidth={1.5} strokeDasharray="2 5" opacity={0.45}
                      />
                    )}
                    {/* vertex dots; the first one becomes the "click to close" target */}
                    {pts.map(([x, y], i) => (
                      <circle
                        key={i}
                        cx={X(x)} cy={Y(y)}
                        r={i === 0 && canClose ? 7 : 4}
                        fill={i === 0 && canClose ? '#fff' : '#2563eb'}
                        stroke="#2563eb" strokeWidth={2}
                      />
                    ))}
                  </g>
                );
              })()}
            </svg>
          )}

          {/* HTML label chips */}
          {size.h > 0 && markers.map((m) => renderLabel(m, resolvedById[m.id], compareById?.[m.id]))}
        </div>
      </div>
    </div>
  );
};

export default GaDashboardCanvas;
